import { useState } from 'react'
import type { ReactNode } from 'react'
import type { JSX } from 'react'

import {
  ArrowLeftIcon,
  Button,
  CloudCheckIcon,
  Flex,
  IconButton,
  InfoIcon,
  Paragraph,
  Popover,
  useIsMobileScreenSize,
} from '@buffer-mono/popcorn'

import { GUEST_MEDIA_TTL_DAYS } from '~publish/helpers/guestMedia'

import styles from './GuestSessionFrame.module.css'
import { resolveGuestReturnUrl } from './guestReturnUrl'

type GuestSessionFrameProps = {
  children: ReactNode
  action?: ReactNode
  inactive?: boolean
}

/**
 * Dark frame around every `/p` page. The strip above the app names the guest
 * session and holds the signup handoff that turns guest work into an account.
 */
export function GuestSessionFrame({
  children,
  action,
  inactive = false,
}: GuestSessionFrameProps): JSX.Element {
  const compact = useIsMobileScreenSize()
  const [returnUrl] = useState(() => resolveGuestReturnUrl())
  const statusClassName = compact
    ? styles.sessionStatus
    : `${styles.sessionStatus} ${styles.desktopSessionStatus}`
  const statusLabel = (
    <>
      <CloudCheckIcon size="small" color="inherit" aria-hidden />
      Guest mode
      <InfoIcon size="small" color="inherit" aria-hidden />
    </>
  )
  return (
    <div className={styles.frame}>
      <div className={compact ? styles.compactSessionBar : styles.sessionBar}>
        {inactive ? null : compact ? (
          <IconButton
            as="a"
            href={returnUrl}
            variant="tertiary"
            size="small"
            label="Back to buffer.com"
            className={styles.backLink}
          >
            <ArrowLeftIcon />
          </IconButton>
        ) : (
          <Button
            as="a"
            href={returnUrl}
            variant="tertiary"
            size="small"
            className={styles.backLink}
          >
            <ArrowLeftIcon />
            Back to buffer.com
          </Button>
        )}
        <Flex
          align="center"
          justify="center"
          gap={compact ? 'xs' : 'sm'}
          className={styles.sessionMessage}
        >
          {inactive ? (
            <span className={statusClassName}>{statusLabel}</span>
          ) : (
            <Popover>
              <Popover.Trigger>
                <button type="button" className={statusClassName}>
                  {statusLabel}
                </button>
              </Popover.Trigger>
              <Popover.Content side="bottom" align="center" size="small">
                <Paragraph size="md">
                  Your work is kept up to {GUEST_MEDIA_TTL_DAYS} days in the
                  same browser. Create an account to keep it and publish to your
                  channels.
                </Paragraph>
              </Popover.Content>
            </Popover>
          )}
          {action}
        </Flex>
      </div>
      <div className={styles.app}>{children}</div>
    </div>
  )
}
